import { useEffect, useRef } from 'react';

/**
 * MAKE A DIALOG DISMISSIBLE, AND ACTUALLY FOCUSED.
 *
 * Every modal in the app hand-rolled this and six of the seven got it wrong the same way (BUGS.md
 * 2026-08-30, found by the creator journey): an `onKeyDown` on the dialog element, and nothing that moves
 * focus into it. React's handler only fires for events inside its own subtree, so opening a modal while focus
 * stays on the button behind the overlay means Escape never reaches the dialog and the modal can only be
 * closed with a mouse.
 *
 * The worse half of the same fault: focus staying outside an `aria-modal="true"` overlay lets Tab walk the
 * page UNDERNEATH it. That attribute is a promise that the rest of the page is inert, and a keyboard user is
 * entitled to rely on it.
 *
 * Two things fix both, and they belong together rather than in seven copies:
 *   - move focus into the dialog when it opens, so it is where the user's attention already is;
 *   - listen for Escape on the DOCUMENT, so it works wherever focus lands inside — a card, a scroll
 *     container, the close button.
 *
 * Usage — spread the returned props onto the dialog element:
 *
 *   const dialog = useDialogDismiss(open, onClose);
 *   return <div role="dialog" aria-modal="true" {...dialog}>…</div>;
 *
 * The element gets `tabIndex={-1}` so it can hold focus without joining the tab ORDER: it is a container,
 * not a control.
 */
export function useDialogDismiss(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // Focus the dialog itself rather than its first control: a modal that opens with a destructive button
    // focused invites an accidental Enter, and the container announces the dialog's label instead.
    ref.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Stop the key here so a modal opened over another surface closes ONE layer, not everything listening.
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return { ref, tabIndex: -1 } as const;
}

export default useDialogDismiss;
